import { Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { MonthlyData } from "../types";
import { TOYOTA_RED, INFO_BLUE, WARNING_ORANGE, SUCCESS_GREEN } from "../theme";
import KpiCard from "../components/KpiCard";
import PlotlyChart from "../components/PlotlyChart";
import ChartCard from "../components/ChartCard";
import TableCard from "../components/TableCard";

interface Props { data: MonthlyData; compact?: boolean; platform?: string; }

const MONTH_ORDER: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

function sortCostData(costData: MonthlyData["costData"]) {
  return [...costData].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return (MONTH_ORDER[a.month] ?? 0) - (MONTH_ORDER[b.month] ?? 0);
  });
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

function fmtTable(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PlatformCost = ({ data, compact, platform }: Props) => {
  const sorted = sortCostData(data.costData);
  const hasMultipleYears = new Set(sorted.map((d) => d.year)).size > 1;
  const months = sorted.map((d) => hasMultipleYears ? `${d.month} ${d.year}` : d.month);
  const databricksValues = sorted.map((d) => d.databricks);
  const awsValues = sorted.map((d) => d.aws);
  const totalValues = sorted.map((d) => d.total);
  const latest = sorted[sorted.length - 1];
  const last6 = sorted.slice(-6);
  const avg = data.sixMonthAvg ?? (last6.reduce((s, d) => s + d.total, 0) / last6.length);

  // Cost optimization savings by year (TDSP), in $K.
  const savings2024 = 301.896;
  const savings2025 = 151.968;
  const savings2026 = 233.0;
  const cumulativeSavings = savings2024 + savings2025 + savings2026;

  const sp = compact ? 1 : 2;
  const chartH = compact ? 260 : 320;

  return (
    <Box>
      {!compact && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#1B2559" }}>
          💰 Platform Cost Analysis
        </Typography>
      )}

      {/* KPI Cards at the top */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Databricks" value={fmt(latest?.databricks || 0)} tooltip={fmtTable(latest?.databricks || 0)} color={INFO_BLUE} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="AWS" value={fmt(latest?.aws || 0)} tooltip={fmtTable(latest?.aws || 0)} color={TOYOTA_RED} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="Total" value={fmt(latest?.total || 0)} tooltip={fmtTable(latest?.total || 0)} color={WARNING_ORANGE} />
        </Grid>
        <Grid item xs={6} sm={compact ? 6 : 3}>
          <KpiCard label="6-Month Avg" value={fmt(avg)} tooltip={fmtTable(avg)} />
        </Grid>
      </Grid>

      {/* Databricks & AWS Historical Trends */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Databricks Cost - Historical Trend">
            <PlotlyChart
              data={[{
                x: months,
                y: databricksValues,
                type: "scatter",
                mode: "lines+markers",
                name: "Cost",
                line: { color: TOYOTA_RED, width: 2 },
                marker: { size: 6 },
              }]}
              layout={{ xaxis: { title: "Month", categoryorder: "array", categoryarray: months }, yaxis: { title: "Cost ($)" }, hovermode: "x unified" }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="AWS Cost - Historical Trend">
            <PlotlyChart
              data={[{
                x: months,
                y: awsValues,
                type: "scatter",
                mode: "lines+markers",
                name: "Cost",
                line: { color: TOYOTA_RED, width: 2 },
                marker: { size: 6 },
              }]}
              layout={{ xaxis: { title: "Month", categoryorder: "array", categoryarray: months }, yaxis: { title: "Cost ($)" }, hovermode: "x unified" }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Total Platform Cost & Monthly Cost Summary */}
      <Grid container spacing={sp} sx={{ mb: 3 }}>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Total Platform Cost - Historical Trend">
            <PlotlyChart
              data={[{
                x: months,
                y: totalValues,
                type: "scatter",
                mode: "lines+markers",
                name: "Cost",
                line: { color: TOYOTA_RED, width: 2 },
                marker: { size: 6 },
              }]}
              layout={{ xaxis: { title: "Month", categoryorder: "array", categoryarray: months }, yaxis: { title: "Cost ($)" }, hovermode: "x unified" }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <TableCard title="Monthly Cost Summary"
            sx={{ overflow: "scroll", height: compact ? 320 : 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Databricks</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>AWS</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{hasMultipleYears ? `${row.month} ${row.year}` : row.month}</TableCell>
                    <TableCell>{fmtTable(row.databricks)}</TableCell>
                    <TableCell>{fmtTable(row.aws)}</TableCell>
                    <TableCell>{fmtTable(row.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>
        </Grid>
      </Grid>

      {/* Cost Optimization — only for TDSP */}
      {platform !== "Enclave" && (<>
      <Typography variant={compact ? "subtitle2" : "h6"} fontWeight={700} sx={{ mb: 2, color: "#1B2559" }}>
        🎯 Cost Optimization
      </Typography>
      <Grid container spacing={sp}>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <ChartCard title="Cost Optimization Savings">
            <PlotlyChart
              data={[{
                x: ["2024", "2025", "2026"],
                y: [savings2024, savings2025, savings2026],
                type: "bar",
                name: "Savings",
                marker: { color: SUCCESS_GREEN },
                text: [`$${savings2024.toFixed(2)}K`, `$${savings2025.toFixed(2)}K`, `$${savings2026.toFixed(2)}K`],
                textposition: "auto",
              }]}
              layout={{ xaxis: { title: "Year" }, yaxis: { title: "Savings ($K)" } }}
              height={chartH}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={compact ? 12 : 6}>
          <Grid container spacing={sp}>
            <Grid item xs={12}>
              <KpiCard label="2024 Savings" value={`$${savings2024.toFixed(2)}K`} delta="↑ Annual" deltaType="positive" color={SUCCESS_GREEN} />
            </Grid>
            <Grid item xs={12}>
              <KpiCard label="2025 Savings" value={`$${savings2025.toFixed(2)}K`} delta="↑ Annual" deltaType="positive" color={SUCCESS_GREEN} />
            </Grid>
            <Grid item xs={12}>
              <KpiCard label="2026 Savings" value={`$${savings2026.toFixed(2)}K`} delta="↑ Annual" deltaType="positive" color={SUCCESS_GREEN} />
            </Grid>
            <Grid item xs={12}>
              <KpiCard label="Cumulative Savings" value={`$${cumulativeSavings.toFixed(2)}K`} delta="↑ Total" deltaType="positive" color={SUCCESS_GREEN} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      </>)}
    </Box>
  );
};

export default PlatformCost;